import { apiUrl } from '../utils/api-url';

export interface RegisterEntry {
  id?: string;
  customerId: string;
  projectId: string;
  type: string;
  data?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface FactData {
  statement: string;
  category: string;
  status: 'confirmed' | 'assumed' | 'unknown';
  source: string;
  confirmedBy?: string;
  confirmedAt?: string;
  blocking: boolean;
  resolution?: string;
  relatedWorkloadIds: string[];
}

export interface WorkloadData {
  name: string;
  type: string;
  currentEnvironment: string;
  migrationStrategy: string;
  priority: string;
  wave: number;
  dependencies: string[];
  targetAzureSku?: string;
  estimatedMonthlyCost?: number;
  status: string;
  components: string[];
  targetService?: string;
  targetSpec?: string;
  sqlDependency: boolean;
  userCount?: number;
  region?: string;
  // Estate server doc ids constituting this workload (RFC §4.5) — populated when the workload
  // is auto-generated from an assessment run; empty for hand-typed workloads.
  serverIds?: string[];
}

export interface DecisionData {
  topic: string;
  context: string;
  options: string[];
  decision: string;
  rationale: string;
  consequences: string;
  date: string;
  decider: string;
  status: string;
  linkedDocumentId?: string;
}

export interface RiskData {
  description: string;
  category: string;
  likelihood: number;
  impact: number;
  riskScore: number;
  mitigation: string;
  owner: string;
  status: string;
  linkedWorkloadId?: string;
  scope?: 'programme' | 'plan1' | 'mailMigration';
  engagementContext?: string;
}

export interface RaciRowData {
  activity: string;
  engagementContext: 'programme' | 'plan1' | 'mailMigration' | string;
  responsibilities: Record<string, string>;
  notes?: string;
}

export interface GovernanceForumData {
  name: string;
  frequency: string;
  chair: string;
  attendees: string;
  purpose: string;
  output?: string;
}

export interface ChangeRequestData {
  title: string;
  category: 'inscope' | 'minor' | 'material';
  impactSummary: string;
  approver: string;
  status: 'proposed' | 'approved' | 'rejected' | 'deferred';
  raisedDate?: string;
  notes?: string;
}

export interface UxPolicyDecisionData {
  controlId: string;
  controlTitle: string;
  sourceStandard: 'CIS' | 'PCI' | 'ISO' | string;
  controlArea: string;
  disposition: 'deploy' | 'defer' | 'exclude';
  rationale?: string;
  signedBy?: string;
  signedDate?: string;
  materialChangeFlag: boolean;
}

/**
 * One obligation — a human commitment the programme depends on (epic #400). Mirrors
 * ObligationEntry in RegisterDocument.cs. Status rule: satisfied requires evidence —
 * never a bare tick; the UI enforces this before allowing status = 'satisfied'.
 */
export interface ObligationData {
  title: string;
  detail?: string;
  owedBy: string;
  owedByEmail?: string;
  /** Programme step/gate ids this blocks (definition-file ids, e.g. "I1.1", "G3"). */
  blocks: string[];
  leadTime?: string;
  failureMode?: string;
  earlyWarningBinding?: string;
  dueDate?: string;
  status: 'open' | 'chased' | 'satisfied' | 'expired';
  lastChasedAt?: string;
  evidence?: string;
  satisfiedAt?: string;
}

export type RegisterType = 'fact' | 'workload' | 'decision' | 'risk' | 'obligation' | 'raci' | 'governance-forum' | 'change-request' | 'ux-policy' | 'stakeholder' | 'skill';

export async function listRegisters(
  customerId: string, projectId: string, type: RegisterType, token: string
): Promise<RegisterEntry[]> {
  const params = new URLSearchParams({ customerId, projectId, type });
  const res = await fetch(apiUrl(`/api/registers?${params}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to list registers: ${res.status}`);
  return res.json();
}

export async function createRegister(entry: RegisterEntry, token: string): Promise<RegisterEntry> {
  const res = await fetch(apiUrl('/api/registers'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(entry),
  });
  if (!res.ok) throw new Error(`Failed to create register: ${res.status}`);
  return res.json();
}

export async function updateRegister(id: string, entry: RegisterEntry, token: string): Promise<RegisterEntry> {
  const res = await fetch(apiUrl(`/api/registers/${id}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(entry),
  });
  if (!res.ok) throw new Error(`Failed to update register: ${res.status}`);
  return res.json();
}

export async function deleteRegister(id: string, customerId: string, token: string): Promise<void> {
  const res = await fetch(apiUrl(`/api/registers/${id}?customerId=${encodeURIComponent(customerId)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to delete register: ${res.status}`);
}
